(() => {
  if (window.QuadroCart) return;
  const drawer = document.querySelector('[data-quadro-cart-drawer]');
  if (!drawer) return;
  const itemsRoot = drawer.querySelector('[data-quadro-cart-items]');
  const errorRoot = drawer.querySelector('[data-quadro-cart-error]');
  const subtotal = drawer.querySelector('[data-quadro-cart-subtotal]');
  const cartUrl = drawer.dataset.cartUrl;
  let previousFocus = null;
  let busy = false;
  let requestVersion = 0;
  let state = null;
  let pageStale = false;
  let needsCartPage = false;
  let work = Promise.resolve();
  let pendingWork = 0;
  let addErrors = [];
  const pendingAdds = new WeakMap();
  const pendingControls = new WeakMap();
  const pageState = document.querySelector('[data-quadro-cart-page-state]');
  const signature = cart => JSON.stringify([cart.total_price, cart.currency, (cart.items || []).map(item => [item.key, item.quantity, item.final_line_price])]);
  let pageSignature = null;
  if (pageState) {
    try { pageSignature = signature(JSON.parse(pageState.textContent)); } catch (error) {}
  }
  const formatters = new Map();
  const escape = value => String(value ?? '').replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]);
  const money = (amount, currency) => {
    const code = currency || drawer.dataset.currency;
    try {
      if (!formatters.has(code)) formatters.set(code, new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: code }));
      return escape(formatters.get(code).format((Number(amount) || 0) / 100));
    } catch (error) { return `${((Number(amount) || 0) / 100).toFixed(2)} ${escape(code)}`; }
  };
  const safeUrl = value => {
    try {
      const url = new URL(value || '#', location.origin);
      return ['https:', 'http:'].includes(url.protocol) ? escape(url.href) : '#';
    } catch (error) { return '#'; }
  };
  const endpoint = name => `${cartUrl.replace(/\/$/, '')}${name ? `/${name}` : ''}.js`;
  const showError = message => {
    errorRoot.textContent = message || drawer.dataset.error;
    errorRoot.hidden = false;
  };
  const setBusy = value => {
    busy = value;
    document.documentElement.classList.toggle('quadro-cart-busy', value);
    drawer.setAttribute('aria-busy', String(value));
    itemsRoot.querySelectorAll('input, button').forEach(control => { control.disabled = value; });
  };
  // Cart mutations share one queue; a product submission must never disappear
  // just because an earlier drawer read or quantity update is still pending.
  const schedule = operation => {
    if (!pendingWork) addErrors = [];
    pendingWork += 1;
    setBusy(true);
    const task = work.then(operation).finally(() => {
      pendingWork -= 1;
      setBusy(pendingWork > 0);
      if (!pendingWork && (needsCartPage || (pageStale && !drawer.open))) location.assign(cartUrl);
    });
    work = task.catch(() => {});
    return task;
  };
  const markPending = (control, value) => {
    if (!control) return;
    let pending = pendingControls.get(control);
    if (value) {
      if (!pending) pending = { count: 0, ariaBusy: control.getAttribute('aria-busy') };
      pending.count += 1;
      pendingControls.set(control, pending);
      control.dataset.quadroCartPending = '';
      control.setAttribute('aria-busy', 'true');
    } else if (pending && --pending.count === 0) {
      delete control.dataset.quadroCartPending;
      if (pending.ariaBusy === null) control.removeAttribute('aria-busy');
      else control.setAttribute('aria-busy', pending.ariaBusy);
      pendingControls.delete(control);
    }
  };
  const open = trigger => {
    if (!drawer.open) {
      previousFocus = trigger || document.activeElement;
      drawer.showModal();
      document.documentElement.classList.add('quadro-cart-open');
    }
  };
  const close = () => drawer.close();
  drawer.addEventListener('close', () => {
    document.documentElement.classList.remove('quadro-cart-open');
    if (pageStale && !busy) { location.assign(cartUrl); return; }
    if (previousFocus?.isConnected) previousFocus.focus();
  });
  drawer.querySelector('[data-quadro-cart-close]').addEventListener('click', close);
  drawer.addEventListener('click', event => {
    if (event.target !== drawer) return;
    const bounds = drawer.getBoundingClientRect();
    if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) close();
  });
  const render = (cart, publish = true) => {
    state = cart;
    if (pageSignature !== null && signature(cart) !== pageSignature) {
      pageStale = true;
      document.documentElement.classList.add('quadro-cart-page-stale');
    }
    errorRoot.hidden = true;
    document.querySelectorAll('[data-cart-count]').forEach(counter => {
      counter.dataset.cartCount = String(cart.item_count || 0);
      counter.textContent = String(cart.item_count || 0);
    });
    subtotal.innerHTML = `${escape(drawer.dataset.subtotal)}: <strong>${money(cart.total_price, cart.currency)}</strong>`;
    drawer.querySelector('[data-quadro-cart-discounts]').innerHTML = (cart.cart_level_discount_applications || []).map(discount => `<p>${escape(discount.title)} (−${money(discount.total_allocated_amount, cart.currency)})</p>`).join('');
    itemsRoot.innerHTML = cart.items?.length ? cart.items.map((item, index) => {
      const title = escape(item.product_title || item.title);
      const key = escape(item.key);
      const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<p class="qcart__variant">${escape(item.variant_title)}</p>` : '';
      const plan = item.selling_plan_allocation?.selling_plan?.name;
      const properties = Object.entries(item.properties || {}).filter(([name, value]) => !name.startsWith('_') && value != null && value !== '').map(([name, value]) => `<p class="qcart__property">${escape(name)}: ${String(value).includes('/uploads/') ? `<a href="${safeUrl(value)}">${escape(String(value).split('/').pop())}</a>` : escape(value)}</p>`).join('');
      const discounts = (item.line_level_discount_allocations || []).map(discount => `<p class="qcart__discount">${escape(discount.discount_application.title)} (−${money(discount.amount, cart.currency)})</p>`).join('');
      const unit = item.unit_price_measurement;
      const unitPrice = unit ? `<small class="qcart__unit-price">${money(item.unit_price, cart.currency)} / ${unit.reference_value !== 1 ? escape(unit.reference_value) : ''}${escape(unit.reference_unit)}</small>` : '';
      let image = '';
      if (item.image) {
        const url = new URL(item.image, location.origin);
        if (url.hostname === 'cdn.shopify.com' || url.hostname.endsWith('.myshopify.com')) url.searchParams.set('width', '160');
        image = `<img src="${safeUrl(url.href)}" alt="${title}" width="80" height="80" loading="lazy">`;
      }
      return `<article class="qcart__item" data-cart-item-index="${index + 1}" data-cart-item-key="${key}">
        <a class="qcart__image" href="${safeUrl(item.url)}">${image}</a>
        <div class="qcart__details">
          <h3><a href="${safeUrl(item.url)}">${title}</a></h3>${variant}${plan ? `<p class="qcart__property">${escape(plan)}</p>` : ''}${properties}${discounts}
          <span class="quadro-cart-slide-badge" data-quadro-slide-badge hidden></span>
          <div class="qcart__line-bottom">
            <div class="qcart__quantity">
              <button type="button" data-quadro-cart-step="-1" aria-label="${escape(drawer.dataset.quantity)}: ${title}, −1">−</button>
              <input type="number" min="0" step="1" inputmode="numeric" value="${Number(item.quantity)}" data-quadro-cart-quantity aria-label="${escape(drawer.dataset.quantity)}: ${title}">
              <button type="button" data-quadro-cart-step="1" aria-label="${escape(drawer.dataset.quantity)}: ${title}, +1">+</button>
            </div>
            <p class="qcart__price">${item.original_line_price !== item.final_line_price ? `<s>${money(item.original_line_price, cart.currency)}</s> ` : ''}${money(item.final_line_price, cart.currency)}${unitPrice}</p>
          </div>
          <button type="button" class="qcart__remove" data-quadro-cart-remove aria-label="${escape(drawer.dataset.remove)}: ${title}"><svg viewBox="0 0 24 24" aria-hidden="true"><path fill="none" d="M3 6h18M9 6V3h6v3M5 6l1 15h12l1-15M10 10v7M14 10v7"/></svg></button>
        </div>
      </article>`;
    }).join('') : `<p class="qcart__empty">${escape(drawer.dataset.empty)}</p>`;
    itemsRoot.querySelectorAll('input, button').forEach(control => { control.disabled = busy; });
    if (addErrors.length) showError(Array.from(new Set(addErrors)).join('\n'));
    if (publish) document.dispatchEvent(new CustomEvent('quadro:cart-updated', { detail: { cart, source: 'quadro-cart' } }));
  };
  const readResponse = async response => {
    let data;
    try { data = await response.json(); } catch (error) { throw new Error(drawer.dataset.error); }
    if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : drawer.dataset.error);
    return data;
  };
  const fetchCart = async () => readResponse(await fetch(endpoint(''), { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } }));
  const refresh = async trigger => {
    open(trigger);
    if (busy) return work;
    const version = ++requestVersion;
    return schedule(async () => {
      try {
        const cart = await fetchCart();
        if (version === requestVersion) render(cart);
      } catch (error) { if (version === requestVersion) showError(error.message); }
    });
  };
  const change = async (row, quantity) => {
    if (busy) return;
    const key = row?.dataset.cartItemKey;
    if (!key) return;
    const focused = document.activeElement;
    const focusSelector = focused?.matches('[data-quadro-cart-step]') ? `[data-quadro-cart-step="${focused.dataset.quadroCartStep}"]` : '[data-quadro-cart-quantity]';
    ++requestVersion;
    await schedule(async () => {
      try {
        const cart = await readResponse(await fetch(endpoint('change'), { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity }) }));
        render(cart);
      } catch (error) {
        if (state) render(state);
        showError(error.message);
      }
    });
    if (!busy && drawer.open) {
      const nextRow = Array.from(itemsRoot.querySelectorAll('[data-cart-item-key]')).find(element => element.dataset.cartItemKey === key);
      (nextRow?.querySelector(focusSelector) || drawer.querySelector('[data-quadro-cart-close]')).focus();
    }
  };
  itemsRoot.addEventListener('click', event => {
    const button = event.target.closest('[data-quadro-cart-step], [data-quadro-cart-remove]');
    if (!button) return;
    const row = button.closest('[data-cart-item-key]');
    const input = row.querySelector('[data-quadro-cart-quantity]');
    const quantity = button.hasAttribute('data-quadro-cart-remove') ? 0 : Math.max(0, (Number.parseInt(input.value, 10) || 0) + Number(button.dataset.quadroCartStep));
    change(row, quantity);
  });
  itemsRoot.addEventListener('change', event => {
    if (!event.target.matches('[data-quadro-cart-quantity]')) return;
    change(event.target.closest('[data-cart-item-key]'), Math.max(0, Number.parseInt(event.target.value, 10) || 0));
  });
  document.addEventListener('click', event => {
    if (pageStale && event.target.closest('.eu-cart-page') && event.target.closest('[name="checkout"], [data-cart-page-update], .eu-cart-page__remove')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!busy) location.assign(cartUrl);
      return;
    }
    const trigger = event.target.closest('[data-cart-drawer-open], .js-go-cart-trigger');
    if (!trigger || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    refresh(trigger);
  });
  document.addEventListener('click', event => {
    if (!busy || !event.target.closest('[name="checkout"], a[href*="/checkout"], [data-quadro-cart-view], [data-cart-page-update], .eu-cart-page__remove')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);
  const add = async (form, button, submittedBody) => {
    const body = submittedBody || new FormData(form);
    const fingerprint = JSON.stringify(Array.from(body, ([key, value]) => [key, value instanceof File ? [value.name, value.size, value.lastModified] : value]));
    let submissions = pendingAdds.get(form);
    if (!submissions) { submissions = new Set(); pendingAdds.set(form, submissions); }
    if (submissions.has(fingerprint)) return;
    submissions.add(fingerprint);
    markPending(form, true);
    markPending(button, true);
    if (!busy) errorRoot.hidden = true;
    open(button);
    await schedule(async () => {
      ++requestVersion;
      let added = false;
      let rejected = false;
      try {
        const response = await fetch(endpoint('add'), { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json' }, body });
        if (!response.ok) { rejected = true; await readResponse(response); }
        // The add response body is unused. Its successful status is enough;
        // the next read supplies the authoritative complete cart.
        added = true;
        render(await fetchCart());
        needsCartPage = false;
      } catch (error) {
        if (added) needsCartPage = true;
        else {
          if (!rejected) {
            // A lost response can still mean Shopify accepted the POST. Read
            // the cart once to reconcile it; never repeat that POST here.
            try { render(await fetchCart()); needsCartPage = false; }
            catch (readError) { needsCartPage = true; }
          }
          addErrors.push(rejected ? error.message : drawer.dataset.error);
          open(button);
          showError(Array.from(new Set(addErrors)).join('\n'));
        }
      } finally {
        submissions.delete(fingerprint);
        if (!submissions.size) pendingAdds.delete(form);
        markPending(form, false);
        markPending(button, false);
      }
    });
  };
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if ((busy || pageStale) && form.matches('[data-quadro-cart-page-form]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!busy && pageStale) location.assign(cartUrl);
      return;
    }
    const action = new URL(form.action, location.origin);
    if (action.origin !== location.origin || !/\/cart\/add(?:\.js)?\/?$/.test(action.pathname)) return;
    if (form.hasAttribute('data-quadro-cart-native')) return;
    const button = event.submitter || form.querySelector('[type="submit"], [data-add-to-cart], [data-add-to-cart-button]');
    if (button?.name === 'checkout') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (button?.disabled || button?.getAttribute('aria-disabled') === 'true' || form.classList.contains('product-form--variant-sold-out') || !form.reportValidity()) return;
    add(form, button, new FormData(form));
  }, true);
  document.addEventListener('change', event => {
    if (!event.target.matches('[data-cart-page-quantity]')) return;
    const form = event.target.closest('[data-quadro-cart-page-form]');
    if (!form || !event.target.checkValidity()) return;
    form.requestSubmit(form.querySelector('[data-cart-page-update]'));
  });
  document.addEventListener('quadro:cart-updated', event => {
    if (event.detail?.source === 'quadro-cart' || !event.detail?.cart) return;
    ++requestVersion;
    render(event.detail.cart, false);
    if (pageStale && !drawer.open && !busy) location.assign(cartUrl);
  });
  document.querySelectorAll('[data-quadro-eu-express]').forEach(container => {
    const sync = () => {
      const provider = container.querySelector('shopify-paypal-button, shopify-google-pay-button, shopify-apple-pay-button, shopify-shop-pay-button, shopify-amazon-pay-button, [data-shopify-buttoncontainer] iframe, [data-shopify-buttoncontainer] button, [data-shopify-buttoncontainer] input, [data-shopify-buttoncontainer] a, [data-shopify-buttoncontainer] [role="button"]');
      if (provider && !provider.closest('.wallet-cart-button__skeleton')) {
        container.dataset.expressCheckoutState = 'ready';
        container.removeAttribute('aria-hidden');
      }
      container.querySelectorAll('shopify-paypal-button, shopify-google-pay-button').forEach(button => {
        if (!button.hasAttribute('data-umami-event')) button.dataset.umamiEvent = button.localName === 'shopify-paypal-button' ? 'cart:pay:paypal-express' : 'cart:pay:google-express';
      });
    };
    new MutationObserver(sync).observe(container, { childList: true, subtree: true });
    sync();
    setTimeout(() => {
      if (container.dataset.expressCheckoutState === 'loading') container.dataset.expressCheckoutState = 'waiting';
    }, 1500);
  });
  window.QuadroCart = { refresh, close, add };
})();
